import React from 'react';
import { flow } from 'lodash';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';

import useIsMobileHook from '@giro-pdv/hooks/useIsMobile.hook';

import HeaderComponent from '@giro-pdv/components/Header.component';

import pinpad from '@giro-pdv/modules/pdv/store/pinpad';
import menu from '@giro-pdv/store/menu';
import useIsTotemHook from '@giro-pdv/hooks/useTotem.hook';
import HeaderEvoraComponent from '@giro-pdv/components/HeaderEvora.component';

import auth from '@giro/shared-store/auth';
import fees from '@giro-pdv/modules/pdv/store/fees';

const AppLayout = ({
  children,
  fullHeight = false,
  maxWidth = 'lg',
}: {
  children: React.ReactNode;
  fullHeight?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}) => {
  const isMobile = useIsMobileHook();
  const isTotem = useIsTotemHook();

  const dispatch = useDispatch();

  const dispatchRedux = {
    updateStatus: flow(pinpad.action.updateStatus, dispatch),
    open: flow(menu.action.open, dispatch),
    close: flow(menu.action.close, dispatch),
    unsetToken: flow(auth.action.unsetToken, dispatch),
  };

  const selectorRedux = {
    status: useSelector(pinpad.selector.selectStatus),
    pinpadError: useSelector(pinpad.selector.selectError),
    menuVisible: useSelector(menu.selector.selectVisible),
    feesData: useSelector(fees.selector.selectData),
  };

  const handleToggleSide = () => {
    if (selectorRedux.menuVisible) {
      dispatchRedux.close();
    }
    if (!selectorRedux.menuVisible) {
      dispatchRedux.open({});
    }
  };

  const hasPix = selectorRedux.feesData?.some((fd) => fd.cdTipoTaxa === 3);

  const open = selectorRedux.status !== null;

  const alertObj = {
    connecting: {
      message: 'Conectando-se ao pinpad...',
      props: {
        severity: 'warning',
      },
    },
    liveness: {
      message: 'Checando conexão com o servidor...',
      props: {
        severity: 'warning',
      },
    },
    error: {
      message:
        selectorRedux.pinpadError?.[1]?.message || 'Pinpad não encontrado',
      props: {
        severity: 'error',
        action: (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              color="inherit"
              size="small"
              onClick={() => dispatch(pinpad.action.servicePostIsPresent())}
            >
              Tentar novamente
            </Button>
            <IconButton
              aria-label="Fechar alerta de pinpad"
              color="inherit"
              size="small"
              onClick={() => dispatchRedux.updateStatus(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    },
    connected: {
      message: 'Pinpad conectado',
      props: {
        severity: 'success',
        onClose: () => dispatchRedux.updateStatus(null),
      },
    },
    busy: {
      message:
        selectorRedux.pinpadError?.[1]?.message ||
        'O pinpad está ocupado com uma operação pendente.',
      props: {
        severity: 'warning',
        action: selectorRedux.pinpadError?.[1]?.pending ? undefined : (
          <Button
            color="inherit"
            size="small"
            onClick={() => dispatch(pinpad.action.servicePostIsPresent())}
          >
            Verificar novamente
          </Button>
        ),
      },
    },
  };

  const menuDown = [
    {
      label: 'Sair',
      onClick: dispatchRedux.unsetToken,
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <use href="/feather-sprite.svg#log-out" />
        </svg>
      ),
    },
  ];

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      {selectorRedux.status !== null && (
        <Alert {...alertObj?.[selectorRedux.status].props}>
          {alertObj[selectorRedux.status]?.message}
        </Alert>
      )}

      <Box id="header-app">
        {!isTotem ? (
          <HeaderComponent
            sideOpen={selectorRedux.menuVisible}
            handleToggleSide={handleToggleSide}
          />
        ) : (
          <HeaderEvoraComponent
            sideOpen={selectorRedux.menuVisible}
            handleToggleSide={handleToggleSide}
          />
        )}
      </Box>

      <Box
        py={{ xs: 2, md: 3 }}
        flex={1}
        minHeight={0}
        id="containerScroll"
        sx={{
          overflowY: 'visible',
          '@media print': {
            height: '100% !important',
          },
        }}
      >
        <Container maxWidth={maxWidth}>{children}</Container>
      </Box>

      <Box bgcolor="trasso.gray.20" flexShrink={0} id="footer-app">
        <Container maxWidth="lg">
          <Box
            minHeight={{ xs: 132, sm: 112, md: 100 }}
            py={{ xs: 2, md: 0 }}
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              flexWrap="wrap"
              minWidth={0}
            >
              <Typography
                variant="trasso_heading_small"
                color="trasso.purple.60"
                whiteSpace="nowrap"
              >
                Bandeiras aceitas:
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="1fr"
                sx={{
                  img: {
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
              >
                {hasPix && <img src="/brands-pix.png" />}
                {!hasPix && <img src="/brands.png" />}
              </Box>
            </Box>
            <Box>
              {menuDown.map((item, index) => (
                <Button
                  key={index}
                  variant="text"
                  size="small"
                  onClick={() => item.onClick()}
                  startIcon={item.icon}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AppLayout;
